// Generic FX textures. Everything here is WHITE (alpha/grey shading only) so the
// FX system can tint it at runtime. Projectile art lives in ./projectiles.ts.

import type Phaser from 'phaser';
import { TEX } from '../keys';
import { TAU, addGridFrames, clamp, fbm, hexPath, newCanvas, noiseOctaves, scratch, seeded } from './canvas';
import { GA, falloff, gauss, paintAlpha, paintGreyAlpha, segDist } from './paint';
import { buildProjectileArt } from './projectiles';

/** Extra FX textures used internally by systems/fx (not part of the shared TEX contract). */
export const FX_EXTRA = {
  /** 48×48 warning glyph: exclamation inside a rounded triangle (white on dark backing). */
  alert: 'fx_alert',
  /** 64×64 four-point flare (glints, impact stars). */
  star: 'fx_star',
  /** 24×40 flame tongue pointing up (burn trails). */
  flame: 'fx_flame',
} as const;

export function buildFxBase(scene: Phaser.Scene): void {
  pixel(scene);
  soft(scene);
  ring(scene);
  spark(scene);
  muzzle(scene);
  smoke(scene);
  ember(scene);
  shard(scene);
  explosion(scene);
  shield(scene);
  rain(scene);
  bolt(scene);
  trailElectric(scene);
  trailBurn(scene);
  trailPlasma(scene);
  trailVoid(scene);
  chevron(scene);
  hex(scene);
  portal(scene);
  reticle(scene);
  alert(scene);
  star(scene);
  flame(scene);
  buildProjectileArt(scene);
}

function pixel(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.pixel, 4, 4);
  if (!c) return;
  c.g.fillStyle = '#ffffff';
  c.g.fillRect(0, 0, 4, 4);
  c.tex.refresh();
}

function soft(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.soft, 64, 64);
  if (!c) return;
  const k = 4.2;
  const base = Math.exp(-k);
  paintAlpha(c.g, 0, 0, 64, 64, (x, y) => {
    const r = Math.hypot(x - 32, y - 32) / 32;
    if (r >= 1) return 0;
    return (Math.exp(-k * r * r) - base) / (1 - base);
  });
  c.tex.refresh();
}

function ring(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.ring, 128, 128);
  if (!c) return;
  const R = 58;
  paintAlpha(c.g, 0, 0, 128, 128, (x, y) => {
    const dist = Math.hypot(x - 64, y - 64);
    const d = Math.abs(dist - R);
    return clamp(gauss(d, 1.3) + 0.32 * gauss(d, 4.2), 0, 1) * falloff(dist, 61, 64);
  });
  c.tex.refresh();
}

function spark(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.spark, 24, 6);
  if (!c) return;
  // Bright head on the right (sparks are rotated to their velocity), long fading tail.
  paintAlpha(c.g, 0, 0, 24, 6, (x, y) => {
    const u = clamp((x - 0.5) / 23, 0, 1);
    const body = Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.08)), 0.7);
    const sigma = 0.45 + 0.85 * body;
    const along = (0.25 + 0.75 * Math.pow(u, 0.8)) * falloff(Math.abs(u - 0.6), 0.3, 0.62);
    return clamp(gauss(y - 3, sigma) * along * 1.25, 0, 1);
  });
  c.tex.refresh();
}

function muzzle(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.muzzle, 64, 40);
  if (!c) return;
  // Star of tapered spikes from the muzzle point (2, 20): one long forward spike,
  // two angled side spikes and short back-flares, plus a hot core.
  const ox = 3;
  const oy = 20;
  const spikes: [number, number, number][] = [
    // angle, length, base half-width
    [0, 60, 8.5],
    [-0.62, 30, 4.2],
    [0.62, 30, 4.2],
    [-1.25, 15, 2.6],
    [1.25, 15, 2.6],
    [-2.3, 7, 2.2],
    [2.3, 7, 2.2],
  ];
  paintAlpha(c.g, 0, 0, 64, 40, (x, y) => {
    const px0 = x - ox;
    const py0 = y - oy;
    let a = 0;
    for (const [ang, len, w] of spikes) {
      const cx = Math.cos(ang);
      const cy = Math.sin(ang);
      const along = px0 * cx + py0 * cy;
      if (along < -2 || along > len) continue;
      const perp = Math.abs(-px0 * cy + py0 * cx);
      const t = clamp(along / len, 0, 1);
      const hw = Math.max(0.35, w * Math.pow(1 - t, 1.15));
      const v = falloff(perp, hw * 0.25, hw) * (1 - t * 0.35);
      if (v > a) a = v;
    }
    const core = gauss(Math.hypot(px0 - 6, py0), 6.5);
    return clamp(Math.max(a, core) + core * 0.3, 0, 1) * falloff(Math.abs(y - 20), 18, 20);
  });
  c.tex.refresh();
}

function smoke(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.smoke, 64, 64);
  if (!c) return;
  // Union of billowing lobes with noisy edges; each lobe is lit from the top-left.
  const lobes: [number, number, number][] = [
    [31, 36, 15],
    [21, 30, 11],
    [42, 29, 11.5],
    [31, 22, 10.5],
    [43, 41, 10],
    [21, 42, 10],
  ];
  const oct = noiseOctaves(seeded('smoke').int(1, 1e6), 16, 64, 64, 3);
  paintGreyAlpha(c.g, 0, 0, 64, 64, (x, y) => {
    const n = fbm(oct, x, y);
    let a = 0;
    let shade = 0.7;
    for (const [bx, by, s] of lobes) {
      const dx = x - bx;
      const dy = y - by;
      const d = Math.hypot(dx, dy) + (n - 0.5) * 0.7 * s;
      const v = falloff(d, s * 0.05, s * 1.15);
      if (v > a) {
        a = v;
        shade = 0.82 + 0.18 * clamp(-(dx + dy) / (s * 1.4), -1, 1) - (d / s) * 0.1;
      }
    }
    GA.alpha = clamp(a * (0.6 + 0.55 * n), 0, 0.92) * falloff(Math.hypot(x - 32, y - 33), 22, 31.5);
    GA.grey = clamp(shade + (n - 0.5) * 0.15, 0.6, 1);
  });
  c.tex.refresh();
}

function ember(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.ember, 8, 8);
  if (!c) return;
  paintAlpha(c.g, 0, 0, 8, 8, (x, y) => {
    const d = Math.hypot(x - 4, y - 4);
    return clamp(gauss(d, 1.1) + 0.45 * gauss(d, 2.2), 0, 1) * falloff(d, 3, 4);
  });
  c.tex.refresh();
}

function shard(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.shard, 12, 8);
  if (!c) return;
  const g = c.g;
  g.fillStyle = '#ffffff';
  g.beginPath();
  g.moveTo(1, 3.5);
  g.lineTo(5, 0.5);
  g.lineTo(11, 2);
  g.lineTo(10, 6.5);
  g.lineTo(3.5, 7.5);
  g.closePath();
  g.fill();
  // Darker lower facet so tinted debris still shows a lit edge.
  g.fillStyle = '#9aa3ad';
  g.beginPath();
  g.moveTo(1.6, 4);
  g.lineTo(10.6, 3.2);
  g.lineTo(10, 6.5);
  g.lineTo(3.5, 7.5);
  g.closePath();
  g.fill();
  g.fillStyle = '#d6dce3';
  g.fillRect(4, 1.5, 5, 1);
  c.tex.refresh();
}

/**
 * Explosion sheet: 8 frames of 128×128 in greyscale. Grey encodes heat (white = hot core,
 * grey = cooling fire), alpha encodes density, so a single tint produces a full fireball.
 */
function explosion(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.explosion, 128 * 8, 128);
  if (!c) return;
  const rng = seeded('explosion');
  // Billowing lobes around a central fireball; positions/sizes are relative to the frame radius.
  const lobes = [
    { a: 0, rho: 0, s: 0.62, hot: 1 },
    ...Array.from({ length: 11 }, (_, i) => ({
      a: (i / 11) * TAU + rng.float(-0.25, 0.25),
      rho: rng.float(0.42, 0.68),
      s: rng.float(0.3, 0.44),
      hot: rng.float(0.55, 0.9),
    })),
  ];
  const rays = Array.from({ length: 9 }, (_, i) => ({ a: (i / 9) * TAU + rng.float(-0.2, 0.2), len: rng.float(0.75, 1.1) }));
  const oct = noiseOctaves(rng.int(1, 1e6), 32, 1024, 128, 4);
  const radius = [20, 33, 43, 50, 54, 57, 59, 60];
  const density = [1, 1, 1, 0.97, 0.9, 0.74, 0.5, 0.26];
  const heatLobe = [1, 0.95, 0.82, 0.64, 0.46, 0.32, 0.22, 0.14];
  const heatCore = [1, 1, 0.88, 0.62, 0.36, 0.2, 0.1, 0.05];

  for (let f = 0; f < 8; f++) {
    const p = f / 7;
    const R = radius[f];
    const rise = p * p * 9;
    const fx0 = f * 128;
    paintGreyAlpha(c.g, fx0, 0, 128, 128, (x, y) => {
      const cx = x - 64;
      const cy = y - 64 + rise;
      const dist = Math.hypot(cx, cy);
      const n = fbm(oct, x + fx0, y);
      let dens = 0;
      let lobeHeat = 0;
      for (const lb of lobes) {
        const spread = 1 + p * 0.2;
        const lx = Math.cos(lb.a) * lb.rho * R * spread;
        const ly = Math.sin(lb.a) * lb.rho * R * spread - p * lb.rho * 9;
        const s = lb.s * R * (1 + p * 0.12);
        const d = Math.hypot(cx - lx, cy - ly) + (n - 0.5) * 0.42 * s;
        const v = falloff(d, s * 0.7, s);
        if (v <= 0) continue;
        if (v > dens) dens = v;
        // Each lobe is brightest in its middle: reads as a billow, not a flat disc.
        const shade = v * lb.hot * (1 - Math.min(1, d / s) ** 2 * 0.6);
        if (shade > lobeHeat) lobeHeat = shade;
      }
      // Late frames tear open into wisps.
      if (p > 0.3) dens *= clamp((n + 0.98 - p) * 3.2, 0, 1);
      dens *= density[f] * falloff(dist, 57, 63);
      // Initial blast rays (first two frames).
      if (f <= 1) {
        const ang = Math.atan2(cy, cx);
        for (const r of rays) {
          let da = Math.abs(ang - r.a);
          if (da > Math.PI) da = TAU - da;
          const span = R * 1.65 * r.len;
          const v = gauss(da * dist, 1.3 + dist * 0.03) * falloff(dist, 0, span) * (f === 0 ? 0.95 : 0.55);
          if (v > dens) dens = v;
        }
      }
      const core = gauss(dist + (n - 0.5) * R * 0.3, R * (0.5 - p * 0.28));
      const hotness = clamp(lobeHeat * heatLobe[f] + core * heatCore[f], 0, 1);
      GA.alpha = clamp(dens, 0, 1);
      GA.grey = 0.24 + 0.76 * hotness;
    });
  }
  addGridFrames(c.tex, 128, 128, 8, 8);
  c.tex.refresh();
}

function shield(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.shield, 128, 128);
  if (!c) return;
  const R = 60;
  // Fresnel body + bright rim.
  paintAlpha(c.g, 0, 0, 128, 128, (x, y) => {
    const dist = Math.hypot(x - 64, y - 64);
    const r = dist / R;
    const rim = gauss(dist - R, 1.5) + 0.3 * gauss(dist - R, 3.6);
    const body = r < 1 ? 0.05 + 0.42 * Math.pow(r, 5) : 0;
    return clamp(rim + body, 0, 1) * falloff(dist, 62, 64);
  });
  // Hex cells, strongest near the rim, masked to the bubble.
  const s = scratch(128, 128);
  const hr = 9;
  const hw = Math.sqrt(3) * hr;
  s.g.strokeStyle = '#ffffff';
  s.g.lineWidth = 1.2;
  for (let row = -1; row < 10; row++) {
    for (let col = -1; col < 9; col++) {
      const x = col * hw + (row % 2 ? hw / 2 : 0) + 4;
      const y = row * hr * 1.5 + 4;
      hexPath(s.g, x, y, hr - 0.8);
      s.g.stroke();
    }
  }
  const mask = s.g.createRadialGradient(64, 64, 0, 64, 64, R);
  mask.addColorStop(0, 'rgba(255,255,255,0.05)');
  mask.addColorStop(0.6, 'rgba(255,255,255,0.16)');
  mask.addColorStop(0.92, 'rgba(255,255,255,0.55)');
  mask.addColorStop(0.985, 'rgba(255,255,255,0.3)');
  mask.addColorStop(1, 'rgba(255,255,255,0)');
  s.g.globalCompositeOperation = 'destination-in';
  s.g.fillStyle = mask;
  s.g.fillRect(0, 0, 128, 128);
  c.g.drawImage(s.c, 0, 0);
  c.tex.refresh();
}

function rain(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.rain, 4, 40);
  if (!c) return;
  paintAlpha(c.g, 0, 0, 4, 40, (x, y) => {
    const along = Math.pow(clamp(y / 40, 0, 1), 1.6);
    return gauss(x - 2, 0.65) * along * 0.95;
  });
  c.tex.refresh();
}

function bolt(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.bolt, 32, 8);
  if (!c) return;
  paintAlpha(c.g, 0, 0, 32, 8, (_x, y) => clamp(gauss(y - 4, 1.15) + 0.28 * gauss(y - 4, 2.4), 0, 1) * falloff(Math.abs(y - 4), 3, 4));
  c.tex.refresh();
}

/** Polyline points for a zig-zag that starts and ends at the same height (tiles along x). */
function zigzag(ys: number[], w: number): [number, number][] {
  return ys.map((y, i) => [(i / (ys.length - 1)) * w, y] as [number, number]);
}

function polyDist(x: number, y: number, pts: [number, number][]): number {
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const d = segDist(x, y, pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1]);
    if (d < best) best = d;
  }
  return best;
}

function trailElectric(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.trailElectric, 64, 24);
  if (!c) return;
  const main = zigzag([12, 5, 17, 8, 16, 4, 19, 9, 12], 64);
  const second = zigzag([12, 16, 9, 15, 7, 16, 10, 17, 12], 64);
  paintAlpha(c.g, 0, 0, 64, 24, (x, y) => {
    const d1 = Math.min(polyDist(x, y, main), polyDist(x - 64, y, main), polyDist(x + 64, y, main));
    const d2 = Math.min(polyDist(x, y, second), polyDist(x - 64, y, second), polyDist(x + 64, y, second));
    const v = gauss(d1, 0.8) + 0.42 * gauss(d1, 2.4) + 0.12 * gauss(d1, 5) + 0.38 * gauss(d2, 0.7) + 0.12 * gauss(d2, 2.2);
    return clamp(v, 0, 1) * falloff(Math.abs(y - 12), 10, 12);
  });
  c.tex.refresh();
}

function trailBurn(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.trailBurn, 64, 24);
  if (!c) return;
  // Flame tongues rising from a hot base band; positions repeat every 64 px.
  const tongues: [number, number, number, number][] = [
    // x, height, half-width, sway
    [5, 15, 6.5, 1],
    [17, 21, 7.5, -1],
    [30, 14, 6, 1],
    [42, 22, 7.8, -1],
    [55, 17, 6.8, 1],
  ];
  paintGreyAlpha(c.g, 0, 0, 64, 24, (x, y) => {
    // Hot base band along the path.
    let a = 0.85 * gauss(y - 18.5, 2.6) * (0.85 + 0.15 * Math.sin((x / 64) * TAU * 4));
    let hot = 0.9 * gauss(y - 19, 1.8);
    for (const [tx, th, tw, sway] of tongues) {
      for (const off of [-64, 0, 64]) {
        const up = 22 - y; // height above the base
        if (up < -2 || up > th) continue;
        const u = clamp(up / th, 0, 1);
        // Round-bottomed tongue that narrows and curls towards its tip.
        const hw = tw * Math.pow(1 - u, 0.85) * (0.55 + 0.45 * Math.sin(Math.min(1, u * 1.8 + 0.25) * Math.PI * 0.5));
        const dx = Math.abs(x - (tx + off) - sway * Math.sin(u * 2.6) * 2.2 * u);
        const v = falloff(dx, hw * 0.25, hw + 0.6) * (1 - u * 0.5);
        if (v > a) a = v;
        hot = Math.max(hot, v * (1 - u) * (1 - dx / (hw + 1)));
      }
    }
    GA.alpha = clamp(a, 0, 1) * falloff(Math.abs(y - 12), 10, 12);
    GA.grey = 0.58 + 0.42 * clamp(hot * 1.5, 0, 1);
  });
  c.tex.refresh();
}

function trailPlasma(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.trailPlasma, 64, 24);
  if (!c) return;
  paintAlpha(c.g, 0, 0, 64, 24, (x, y) => {
    const wave = Math.sin((x / 64) * TAU) * 1.2;
    const dy = y - 12 - wave;
    const body = gauss(dy, 3.4) * (0.82 + 0.18 * Math.cos((x / 32) * TAU));
    const core = 0.75 * gauss(dy, 1.1);
    return clamp(body * 0.75 + core, 0, 1) * falloff(Math.abs(y - 12), 10, 12);
  });
  c.tex.refresh();
}

function trailVoid(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.trailVoid, 64, 24);
  if (!c) return;
  const oct = noiseOctaves(seeded('void-trail').int(1, 1e6), 16, 64, 32, 3);
  paintGreyAlpha(c.g, 0, 0, 64, 24, (x, y) => {
    const n = fbm(oct, x, y * 1.3);
    const win = gauss(y - 12, 5.2);
    GA.alpha = clamp((n - 0.32) * 2.2, 0, 1) * win * falloff(Math.abs(y - 12), 9, 12);
    GA.grey = 0.7 + 0.3 * n;
  });
  c.tex.refresh();
}

function chevron(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.chevron, 32, 32);
  if (!c) return;
  const g = c.g;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.strokeStyle = 'rgba(255,255,255,0.35)';
  g.lineWidth = 9;
  g.beginPath();
  g.moveTo(6, 9);
  g.lineTo(16, 21);
  g.lineTo(26, 9);
  g.stroke();
  g.strokeStyle = '#ffffff';
  g.lineWidth = 5.5;
  g.stroke();
  c.tex.refresh();
}

function hex(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.hex, 32, 32);
  if (!c) return;
  const g = c.g;
  hexPath(g, 16, 16, 13.5);
  g.fillStyle = 'rgba(255,255,255,0.16)';
  g.fill();
  g.strokeStyle = '#ffffff';
  g.lineWidth = 2.2;
  g.lineJoin = 'round';
  g.stroke();
  c.tex.refresh();
}

function portal(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.spawnPortal, 128, 128);
  if (!c) return;
  const g = c.g;
  const cx = 64;
  const cy = 64;
  g.lineCap = 'round';
  // Faint inner disc.
  const disc = g.createRadialGradient(cx, cy, 0, cx, cy, 50);
  disc.addColorStop(0, 'rgba(255,255,255,0.25)');
  disc.addColorStop(0.6, 'rgba(255,255,255,0.08)');
  disc.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = disc;
  g.fillRect(0, 0, 128, 128);
  // Segmented outer ring (glow + core).
  for (let i = 0; i < 6; i++) {
    const a0 = (i / 6) * TAU + 0.12;
    const a1 = a0 + TAU / 6 - 0.3;
    g.beginPath();
    g.arc(cx, cy, 56, a0, a1);
    g.strokeStyle = 'rgba(255,255,255,0.25)';
    g.lineWidth = 8;
    g.stroke();
    g.strokeStyle = '#ffffff';
    g.lineWidth = 3.5;
    g.stroke();
  }
  // Tick marks.
  g.strokeStyle = 'rgba(255,255,255,0.7)';
  g.lineWidth = 1.6;
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * TAU;
    const r0 = i % 2 ? 47 : 45;
    g.beginPath();
    g.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
    g.lineTo(cx + Math.cos(a) * 51, cy + Math.sin(a) * 51);
    g.stroke();
  }
  // Thin inner ring.
  g.beginPath();
  g.arc(cx, cy, 41, 0, TAU);
  g.strokeStyle = 'rgba(255,255,255,0.8)';
  g.lineWidth = 1.5;
  g.stroke();
  // Spiral arms sweeping into the centre.
  for (let k = 0; k < 3; k++) {
    g.beginPath();
    for (let s = 0; s <= 24; s++) {
      const t = s / 24;
      const a = (k / 3) * TAU + t * 2.4;
      const r = 38 * (1 - t) + 6;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (s === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.strokeStyle = 'rgba(255,255,255,0.22)';
    g.lineWidth = 7;
    g.stroke();
    g.strokeStyle = 'rgba(255,255,255,0.75)';
    g.lineWidth = 2.4;
    g.stroke();
  }
  c.tex.refresh();
}

function reticle(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.reticle, 48, 48);
  if (!c) return;
  const g = c.g;
  const cx = 24;
  const cy = 24;
  const draw = (stroke: string, w: number) => {
    g.strokeStyle = stroke;
    g.lineWidth = w;
    g.lineCap = 'round';
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + Math.PI / 4;
      g.beginPath();
      g.arc(cx, cy, 10, a - 0.5, a + 0.5);
      g.stroke();
      const t = (i / 4) * TAU;
      g.beginPath();
      g.moveTo(cx + Math.cos(t) * 14, cy + Math.sin(t) * 14);
      g.lineTo(cx + Math.cos(t) * 20.5, cy + Math.sin(t) * 20.5);
      g.stroke();
    }
  };
  // Dark outline keeps the white crosshair readable over bright explosions.
  draw('rgba(8,10,16,0.75)', 5);
  draw('#ffffff', 2.4);
  g.fillStyle = 'rgba(8,10,16,0.75)';
  g.beginPath();
  g.arc(cx, cy, 3.4, 0, TAU);
  g.fill();
  g.fillStyle = '#ffffff';
  g.beginPath();
  g.arc(cx, cy, 1.9, 0, TAU);
  g.fill();
  c.tex.refresh();
}

function alert(scene: Phaser.Scene): void {
  const c = newCanvas(scene, FX_EXTRA.alert, 48, 48);
  if (!c) return;
  const g = c.g;
  const tri = () => {
    g.beginPath();
    g.moveTo(24, 5);
    g.lineTo(44, 41);
    g.lineTo(4, 41);
    g.closePath();
  };
  g.lineJoin = 'round';
  tri();
  g.fillStyle = 'rgba(10,12,18,0.72)';
  g.fill();
  g.strokeStyle = 'rgba(255,255,255,0.35)';
  g.lineWidth = 8;
  g.stroke();
  g.strokeStyle = '#ffffff';
  g.lineWidth = 4;
  g.stroke();
  g.fillStyle = '#ffffff';
  g.beginPath();
  g.moveTo(21.4, 16);
  g.lineTo(26.6, 16);
  g.lineTo(25.4, 29);
  g.lineTo(22.6, 29);
  g.closePath();
  g.fill();
  g.beginPath();
  g.arc(24, 34.5, 2.6, 0, TAU);
  g.fill();
  c.tex.refresh();
}

function star(scene: Phaser.Scene): void {
  const c = newCanvas(scene, FX_EXTRA.star, 64, 64);
  if (!c) return;
  paintAlpha(c.g, 0, 0, 64, 64, (x, y) => {
    const dx = x - 32;
    const dy = y - 32;
    const r = Math.hypot(dx, dy);
    const spikeH = gauss(dy, 0.9 + Math.abs(dx) * 0.02) * falloff(Math.abs(dx), 4, 31);
    const spikeV = gauss(dx, 0.9 + Math.abs(dy) * 0.02) * falloff(Math.abs(dy), 4, 31);
    const core = gauss(r, 3.2) + 0.35 * gauss(r, 8);
    return clamp(Math.max(spikeH, spikeV) + core, 0, 1);
  });
  c.tex.refresh();
}

function flame(scene: Phaser.Scene): void {
  const c = newCanvas(scene, FX_EXTRA.flame, 24, 40);
  if (!c) return;
  paintGreyAlpha(c.g, 0, 0, 24, 40, (x, y) => {
    const up = 36 - y; // height above the flame base
    const u = clamp(up / 34, 0, 1);
    const hw = 9 * Math.pow(1 - u, 0.9) * (0.55 + 0.45 * Math.sin(Math.min(1, u * 2.2) * Math.PI * 0.5 + 0.6));
    const dx = Math.abs(x - 12 - Math.sin(u * 4) * 1.4 * u);
    const body = up < -4 ? 0 : falloff(dx, hw * 0.35, hw + 1) * falloff(-up, -4, 4);
    GA.alpha = clamp(body * (1 - u * 0.4), 0, 1);
    GA.grey = 0.65 + 0.35 * clamp((1 - u) * 1.3 - dx / 12, 0, 1);
  });
  c.tex.refresh();
}
